import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Slot } from 'radix-ui';
import { Spinner } from '@/components/ui/spinner';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 text-nowrap tracking-[-0.02em] rounded-[12px] cursor-pointer transition-all outline-none active:scale-[0.97] focus-visible:border-brand-ring focus-visible:ring-[3px] focus-visible:ring-brand-ring/50 disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-main-green text-white hover:bg-main-green/90',
        secondary: 'bg-[#EAEAEA] hover:bg-[#EAEAEA]/80',
        destructive:
          'bg-brand-destructive/10 text-brand-destructive hover:bg-brand-destructive/20 focus-visible:border-brand-destructive/40 focus-visible:ring-brand-destructive/20',
        outline: 'border border-main-green bg-white text-text-primary-day hover:bg-main-green/10',
        ghost: 'text-text-primary hover:bg-brand-accent hover:text-brand-accent-foreground',
        link: 'text-main-green underline-offset-4 hover:underline',
        blue: 'bg-[#E5EBFC] text-[#0040E4] hover:bg-[#E5EBFC]/90',
        'green-secondary': 'bg-main-green-white text-main-green hover:bg-main-green-white/90',
      },
      size: {
        sm: 'px-[9px] text-[13px] h-[48px] font-medium',
        'sm-compact': 'px-[8px] text-[14px] h-[32px] font-normal',
        md: 'px-[12px] text-[16px] h-[48px] font-medium',
        full: 'w-full text-[16px] h-[48px] font-medium',
        icon: 'size-8',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

function Button({
  className,
  variant,
  size,
  fullWidth,
  loading = false,
  disabled,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot='button'
      data-variant={variant}
      data-size={size}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    >
      {loading ? <Spinner className='size-5' /> : children}
    </Comp>
  );
}

export { Button, buttonVariants };
